import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  StudentsIcon,
  RegisterIcon,
  CalendarIcon,
  ViewIcon,
  DeleteIcon,
  FeeIcon,
  ArrowIcon,
} from "../icons/Icons";
import { API_BASE_URL, getAuthHeaders } from "../../../utils/api";

function DashboardHome({
  students,
  apiStats,
  permissions,
  feeAssignments = {},
}) {
  const navigate = useNavigate();
  const [campusName, setCampusName] = useState("");
  let cardIndex = 0;

  useEffect(() => {
    if (!permissions?.isAccountant && !permissions?.isAdmin && !permissions?.isAdministrator) {
      setCampusName("");
      return undefined;
    }
    let cancelled = false;
    const loadCampus = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/campuses`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !Array.isArray(data) || !data.length) return;
        setCampusName(data.map((c) => c.name).filter(Boolean).join(", "));
      } catch (err) {
        console.error("Failed to load campus for dashboard", err);
      }
    };
    loadCampus();
    return () => {
      cancelled = true;
    };
  }, [
    permissions?.isAccountant,
    permissions?.isAdmin,
    permissions?.isAdministrator,
  ]);

  const studentsWithoutFeesCount = students.filter((student) => {
    const sid = String(student.id || student.student_id || "");
    const assignments = feeAssignments[sid] || {};
    return Object.keys(assignments).length === 0;
  }).length;
  const totalStudents = Number(apiStats?.total_students ?? students.length);
  const activeStudents = Number(apiStats?.active_students ?? 0);
  const newThisMonth = Number(apiStats?.new_this_month ?? 0);
  const withdrawnStudents = Number(apiStats?.withdrawn_count ?? 0);
  const inactiveStudents = Math.max(
    totalStudents - activeStudents - withdrawnStudents,
    0,
  );
  const stats = [
    {
      icon: <StudentsIcon />,
      color: "blue",
      value: totalStudents,
      label: "Total Students",
    },
    {
      icon: <RegisterIcon />,
      color: "green",
      value: activeStudents,
      label: "Active Students",
    },
    {
      icon: <CalendarIcon />,
      color: "gold",
      value: newThisMonth,
      label: "New This Month",
    },
    {
      icon: <ViewIcon />,
      color: "red",
      value: withdrawnStudents,
      label: "Withdrawn",
    },
  ];

  const statusChartData = [
    { name: "Active", value: activeStudents, color: "#15803d" },
    { name: "Withdrawn", value: withdrawnStudents, color: "#b91c1c" },
    { name: "Other", value: inactiveStudents, color: "#64748b" },
  ].filter((item) => item.value > 0);

  const classChartData = Object.entries(
    students.reduce((acc, student) => {
      const className =
        student.class_name ||
        student.className ||
        student.class_ ||
        student.class_id ||
        "Unassigned";
      acc[className] = (acc[className] || 0) + 1;
      return acc;
    }, {}),
  )
    .map(([name, value]) => ({ name: String(name), students: value }))
    .sort((a, b) => b.students - a.students)
    .slice(0, 8);

  const movementChartData = [
    { name: "Total", value: totalStudents, color: "#2c4777" },
    { name: "Active", value: activeStudents, color: "#15803d" },
    { name: "New", value: newThisMonth, color: "#b8860b" },
    { name: "Withdrawn", value: withdrawnStudents, color: "#b91c1c" },
  ];

  return (
    <div className="dashboard-content dashboard-home">
      <section className="dashboard-overview-hero">
        <div className="dashboard-overview-copy">
          <span className="dashboard-overview-kicker">American Lycetuff</span>
          <h2>
            {(permissions?.isAccountant || permissions?.isAdministrator) &&
            campusName
              ? campusName
              : "Dashboard Overview"}
          </h2>
          <p>
            {(permissions?.isAccountant || permissions?.isAdministrator) &&
            campusName
              ? permissions.isAccountant
                ? `Fee management dashboard for ${campusName}.`
                : `Campus administration dashboard for ${campusName}.`
              : "Monitor enrollment, fee setup, and daily school activity from one operational view."}
          </p>
        </div>
        <div className="dashboard-overview-meta">
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            month: "short",
            day: "numeric",
          })}
        </div>

        <div className="dashboard-hero-stat-row">
          {stats.map((stat, i) => (
            <div
              className={`dashboard-hero-stat ${stat.color}`}
              key={stat.label}
            >
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
              <div>{stat.icon}</div>
            </div>
          ))}
        </div>
      </section>

      {permissions.canViewFees && studentsWithoutFeesCount > 0 && (
        <section className="dashboard-fee-alert">
          <div>
            <strong>
              {studentsWithoutFeesCount} student
              {studentsWithoutFeesCount !== 1 ? "s" : ""} have no fee assigned.
            </strong>
            <span>
              Set up fees now so campus admins can complete the full workflow.
            </span>
          </div>
          <button onClick={() => navigate("/fees")} type="button">
            Set up fees
          </button>
        </section>
      )}

      {/* <section className="dashboard-section">
        <div className="dashboard-section-header">
          <div>
            <span className="dashboard-section-kicker">Analytics</span>
            <h2>Reports Overview</h2>
          </div>
        </div>

        <div className="dashboard-chart-grid">
          <div className="dashboard-chart-card">
            <div className="dashboard-chart-header">
              <h3>Student Status</h3>
              <span>{totalStudents} total</span>
            </div>
            {statusChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={statusChartData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={58}
                    outerRadius={92}
                    paddingAngle={3}
                  >
                    {statusChartData.map((item) => (
                      <Cell key={item.name} fill={item.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="dashboard-chart-empty">No student data yet.</div>
            )}
          </div>

          <div className="dashboard-chart-card">
            <div className="dashboard-chart-header">
              <h3>Students By Class</h3>
              <span>Top classes</span>
            </div>
            {classChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={classChartData}
                  margin={{ top: 8, right: 12, bottom: 12, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5ecf5" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar
                    dataKey="students"
                    fill="#2c4777"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="dashboard-chart-empty">No class data yet.</div>
            )}
          </div>

          <div className="dashboard-chart-card dashboard-chart-card-wide">
            <div className="dashboard-chart-header">
              <h3>Student Movement</h3>
              <span>Current period</span>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={movementChartData}
                margin={{ top: 8, right: 12, bottom: 8, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5ecf5" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {movementChartData.map((item) => (
                    <Cell key={item.name} fill={item.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section> */}

      <section className="dashboard-section">
        <div className="dashboard-section-header">
          <div>
            <span className="dashboard-section-kicker">Frequent Tasks</span>
            <h2>Quick Actions</h2>
          </div>
        </div>

        <div className="action-cards dashboard-action-grid">
          {permissions.canRegisterStudents && (
            <button
              type="button"
              className="action-card"
              style={{
                "--i": ++cardIndex,
                "--accent-color": "var(--green-500)",
              }}
              onClick={() => navigate("/students/register")}
              id="action-register"
            >
              <div className="action-card-icon register">
                <RegisterIcon />
              </div>
              <div className="card-arrow">
                <ArrowIcon />
              </div>
              <h3>Register Student</h3>
              <p>
                Add a new student to the school system with complete details.
              </p>
            </button>
          )}
          {permissions.canDeleteStudents && (
            <button
              type="button"
              className="action-card"
              style={{ "--i": ++cardIndex, "--accent-color": "var(--red-500)" }}
              onClick={() => navigate("/students/bulk-delete")}
              id="action-delete"
            >
              <div className="action-card-icon delete">
                <DeleteIcon />
              </div>
              <div className="card-arrow">
                <ArrowIcon />
              </div>
              <h3>Delete Student</h3>
              <p>Search and remove student records from the system.</p>
            </button>
          )}
          {permissions.canViewStudents && (
            <button
              type="button"
              className="action-card"
              style={{
                "--i": ++cardIndex,
                "--accent-color": "var(--navy-500)",
              }}
              onClick={() => navigate("/students/manage")}
              id="action-view"
            >
              <div className="action-card-icon view">
                <ViewIcon />
              </div>
              <div className="card-arrow">
                <ArrowIcon />
              </div>
              <h3>View Records</h3>
              <p>Browse and search all student records and academic data.</p>
            </button>
          )}
          {permissions.canViewFees && (
            <button
              type="button"
              className="action-card"
              style={{
                "--i": ++cardIndex,
                "--accent-color": "var(--gold-500)",
              }}
              onClick={() => navigate("/fees")}
              id="action-fee"
            >
              <div className="action-card-icon fee">
                <FeeIcon />
              </div>
              <div className="card-arrow">
                <ArrowIcon />
              </div>
              <h3>Fee Management</h3>
              <p>Track fee payments, generate receipts, and manage dues.</p>
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

export default DashboardHome;
